import { z } from "zod";

import { ManifestIdSchema, VersionStringSchema } from "../manifest";

/**
 * Current wire contract version for deployed-flow introspection snapshots.
 *
 * Every level of the schema is strict, so any shape change needs a new
 * version. Producers keep serving the previous version by default until every
 * reader requests the new one with
 * {@link DEPLOYED_FLOW_INTROSPECTION_VERSION_PARAM}.
 */
export const DEPLOYED_FLOW_INTROSPECTION_SCHEMA_VERSION = 2;

/** Query parameter a reader sets to request a specific schema version. */
export const DEPLOYED_FLOW_INTROSPECTION_VERSION_PARAM = "schemaVersion";

const IntrospectionModuleRefSchema = z
  .object({
    moduleId: ManifestIdSchema,
    minVersion: VersionStringSchema,
  })
  .strict();

const IntrospectionViewRefSchema = z
  .object({
    viewId: ManifestIdSchema,
    minVersion: VersionStringSchema,
  })
  .strict();

const IntrospectionBranchSchema = z
  .object({
    branchId: ManifestIdSchema,
    version: VersionStringSchema,
    views: z.array(IntrospectionViewRefSchema),
  })
  .strict();

const IntrospectionFlowV1Schema = z
  .object({
    flowId: z.string().min(1),
    layout: IntrospectionModuleRefSchema,
    errorView: IntrospectionModuleRefSchema,
    branches: z.array(IntrospectionBranchSchema),
  })
  .strict();

const IntrospectionFlowSchema = IntrospectionFlowV1Schema.extend({
  /** The flow's default loading module; omitted when the flow sets none. */
  loading: IntrospectionModuleRefSchema.optional(),
});

/**
 * Schema version 1, still served by default to readers that predate
 * `loading` (their strict parser rejects the field).
 */
export const DeployedFlowIntrospectionV1Schema = z
  .object({
    schemaVersion: z.literal(1),
    revision: z.string().min(1).nullable(),
    flows: z.array(IntrospectionFlowV1Schema),
  })
  .strict();

/**
 * Minimal, unauthenticated snapshot of the flows embedded in a deployment.
 *
 * Deliberately excludes module props, flow metadata, host mappings, and all
 * carrier/customer data.
 */
export const DeployedFlowIntrospectionSchema =
  DeployedFlowIntrospectionV1Schema.extend({
    schemaVersion: z.literal(DEPLOYED_FLOW_INTROSPECTION_SCHEMA_VERSION),
    flows: z.array(IntrospectionFlowSchema),
  });
